import { NavLink, Outlet, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { PageContent } from "../../components/PageContent";
import colors from "@styling/colors";
import { borderRadius, spacing } from "@styling/sizing";
import type { StoreId } from "@KassaroAB/shared/types/ids";
import { Features } from "@KassaroAB/shared/types/enums/Features";
import { useGetActiveFeaturesQuery } from "../../query/billingItemsQuery";

const Layout = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${spacing.x3}px;
  align-items: flex-start;
`;

const SubNav = styled.nav`
  flex: 1 1 180px;
  max-width: 220px;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x05}px;
`;

const SubNavLink = styled(NavLink)`
  padding: ${spacing.x1}px ${spacing.x2}px;
  border-radius: ${borderRadius.regular}px;
  border: 2px solid transparent;
  color: ${colors.text};
  text-decoration: none;

  &.active {
    border-color: ${colors.primary};
    background: ${colors.backgroundLight};
    font-weight: 600;
  }
`;

const Content = styled.div`
  flex: 999 1 480px;
  min-width: 0;
`;

export const StoreSettingsLayout = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const { data: activeFeatures } = useGetActiveFeaturesQuery(storeId ?? null);
  const showStock =
    activeFeatures?.includes(Features.INVENTORY_COSTING) ?? false;
  const showPublicApi = activeFeatures?.includes(Features.PUBLIC_API) ?? false;

  return (
    <PageContent title={t("storeSettings.title")}>
      <Layout>
        <SubNav aria-label={t("storeSettings.title")}>
          <SubNavLink to={`/stores/${storeId}/settings/general`}>
            {t("storeSettings.general")}
          </SubNavLink>
          {showStock && (
            <SubNavLink to={`/stores/${storeId}/settings/stock-valuation`}>
              {t("storeSettings.stock")}
            </SubNavLink>
          )}
          {showPublicApi && (
            <>
              <SubNavLink to={`/stores/${storeId}/settings/api`}>
                {t("storeSettings.api")}
              </SubNavLink>
              <SubNavLink to={`/stores/${storeId}/settings/webhooks`}>
                {t("storeSettings.webhooksNav")}
              </SubNavLink>
            </>
          )}
        </SubNav>
        <Content>
          <Outlet />
        </Content>
      </Layout>
    </PageContent>
  );
};
